import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

class Utils {
  /** Copy text into clipboard
   * @param text Text to be copied
   * @returns Promise<void>
   */

  static async copyToClipboard(text: any): Promise<boolean> {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      return false;
    }
  }

  /** Download file */
  static async downloadFile(file: string, fileName: string) {
    try {
      // Try fetching the asset and creating a blob for broad compatibility
      const res = await fetch(file);
      const blob = await res.blob();

      // IE / old Edge
      if ((window.navigator as any)?.msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // release object URL
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      // Fallback: open the asset URL in a new tab/window
      window.open(file, '_blank');
    }
  }

  /** Open URL in a new tab/window */
  static async openInNewTab(url: string) {
    try {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (e) {
      console.error('Failed to open URL in new tab: ', e);
    }
  }

  /** Open WhatsApp URL in a new tab/window */
  static async openWhatsApp(url?: string) {
    url = url || 'https://api.whatsapp.com/send/?phone=966112061411';
    try {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (e) {
      console.error('Failed to open WhatsApp URL in new tab: ', e);
    }
  }

  /** Validate email format */
  static isEmailValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email ? emailRegex.test(email) : true;
  }

  /** Check if the string is a valid password based on the following criteria: */
  static isValidPassword(password: string): boolean {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    return passwordRegex.test(password);
  }

  static isValidNID(national_id: string): boolean {
    return (
      (national_id.startsWith('1') || national_id.startsWith('2')) &&
      national_id.length === 10
    );
  }

  static isMobileValid(mobile: string): boolean {
    // Phone start with: +9665 and 13 digits in total
    const mobileRegex = /^\+9665\d{8}$/;
    return mobile ? mobileRegex.test(mobile) : true;
  }
}

export default Utils;
